import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, shareReplay, tap, throwError } from 'rxjs';
import type {
  RegisterRequest,
  RegisterResponse,
  LoginRequest,
  LoginResponse,
  MeResponse,
  VerifyEmailRequest,
  ResendVerificationRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
} from './auth.model';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private _token: string | null = null;
  private readonly _isAuthenticated = signal(false);
  readonly isAuthenticated = this._isAuthenticated.asReadonly();

  private refreshInFlight$: Observable<string> | null = null;
  private readonly authChannel = new BroadcastChannel('auth');

  constructor() {
    this.authChannel.onmessage = ({ data }) => {
      if (data === 'logout') {
        this._token = null;
        this._isAuthenticated.set(false);
        void this.router.navigate(['/auth/login']);
      }
    };
  }

  isTokenExpiringSoon(): boolean {
    if (!this._token) return false;
    try {
      const { exp } = JSON.parse(atob(this._token.split('.')[1])) as { exp: number };
      return exp * 1000 - Date.now() < 60_000;
    } catch {
      return true;
    }
  }

  register(payload: RegisterRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(`${environment.apiBaseUrl}/auth/register`, payload);
  }

  login(payload: LoginRequest): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${environment.apiBaseUrl}/auth/login`, payload, { withCredentials: true })
      .pipe(
      tap((res) => {
        this._token = res.accessToken;
        this._isAuthenticated.set(true);
      }),
    );
  }

  me(): Observable<MeResponse> {
    return this.http.get<MeResponse>(`${environment.apiBaseUrl}/auth/me`);
  }

  refresh(): Observable<string> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    this.refreshInFlight$ = this.http
      .post<{ accessToken: string }>(
        `${environment.apiBaseUrl}/auth/refresh`,
        {},
        { withCredentials: true },
      )
      .pipe(
        map((res) => res.accessToken),
        tap((token) => {
          this._token = token;
          this._isAuthenticated.set(true);
        }),
        catchError((err) => {
          this.clearSession();
          return throwError(() => err);
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay(1),
      );

    return this.refreshInFlight$;
  }

  logout(): Observable<void> {
    return this.http
      .post<void>(`${environment.apiBaseUrl}/auth/logout`, {}, { withCredentials: true })
      .pipe(
        finalize(() => {
          this.clearSession();
          this.authChannel.postMessage('logout');
        }),
      );
  }

  clearSession(): void {
    this._token = null;
    this._isAuthenticated.set(false);
  }

  getToken(): string | null {
    return this._token;
  }

  verifyEmail(payload: VerifyEmailRequest): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${environment.apiBaseUrl}/auth/verify-email`,
      payload,
    );
  }

  resendVerification(payload: ResendVerificationRequest): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${environment.apiBaseUrl}/auth/resend-verification`,
      payload,
    );
  }

  forgotPassword(payload: ForgotPasswordRequest): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${environment.apiBaseUrl}/auth/forgot-password`,
      payload,
    );
  }

  resetPassword(payload: ResetPasswordRequest): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${environment.apiBaseUrl}/auth/reset-password`,
      payload,
    );
  }
}
